import ConflictCheckCard from './conflictCheckCard';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Spin } from '../Ant';
import { useFetchConflictChecks } from '../../hooks/useFetchConflictChecks';
import { IConflictCheck } from '../../interface/conflictCheck';
import NoData from '../dashboard/noData';
import { SearchEmpty } from '../icons';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IFilterOption } from '../../interface/transaction';
import { ConflictCheckFilterType } from '../../constants/conflictSearch';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const ConflictCheckListing = ({ type }: { type: string }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption[]>([]);
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'createdOn',
    direction: 'DESC',
  });

  const sortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'createdOn',
        label: t('conflict:createdOn'),
        type: 'date',
        defaultDirection: 'DESC',
      },
      {
        field: 'responsibleUser.fullName',
        label: t('conflict:responsible'),
        type: 'text',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );
  const {
    data: conflictChecksList,
    isLoading: isConflictChecksLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchConflictChecks({
    limit: 10,
    search: searchValue,
    type: type,
    customerId: id,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    extraFilters: {
      ...(selectedFilter?.length > 0 && {
        statusCode: `$in:${selectedFilter.map((x) => x.value).join(',')}`,
      }),
    },
    relations: ['responsibleUser:fullName', 'checkedByUser:fullName'],
    fields: ['createdOn', 'statusCode', 'subject'],
  });
  const conflictCheckFilterOptions: IFilterOption[] = [
    { label: t('conflict:status.accepted'), value: ConflictCheckFilterType.ACCEPTED },
    { label: t('conflict:status.inprogress'), value: ConflictCheckFilterType.IN_PROGRESS },
    { label: t('conflict:status.rejected'), value: ConflictCheckFilterType.REJECTED },
  ];
  const { ref, inView } = useInView({
    threshold: 0.5,
    initialInView: false,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && conflictChecksList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="conflict-check-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('conflict:conflictCheck')}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          filterTitle={t('conflict:conflictCheckFilters')}
          checkboxOptions={conflictCheckFilterOptions}
          selectedFilter={selectedFilter}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            setSelectedFilter(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['conflict-checks'],
            });
            return refetch();
          }}
          clearFilter={(val?: string) => {
            if (val) {
              setSelectedFilter((prev) => prev.filter((item) => item.value !== val));
            } else {
              setSelectedFilter([]);
            }
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={sortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {conflictChecksList.map((val: IConflictCheck) => {
        return <ConflictCheckCard key={val.id} conflictCheck={val} />;
      })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!conflictChecksList ||
        (Object.entries(conflictChecksList).length === 0 && !isConflictChecksLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('conflict:noConflictChecks')} icon={<SearchEmpty />} />
        </div>
      )}
      {isConflictChecksLoading ? <SkeletonCardList rows={[1, 1, 2]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default ConflictCheckListing;
